import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';
import { HeadingText10, pxToRem } from '@modernhealth/vital-components';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MeditationNode, MessageResourceInput, ProgramNode } from 'src/generated/webapp_gql';
import { BenefitModal } from 'src/modules/benefits/components/BenefitModal';
import { CompanyAdditionalBenefitFragment } from 'src/modules/benefits/documents/getCompanyAdditionalBenefitsQuery.generated';
import { Benefit } from 'src/modules/benefits/models/benefits';
import { getCompanyAdditionalBenefitIdsMap } from 'src/modules/benefits/selectors/benefitsSelectors';

import audioIcon from '../assets/audioIcon.svg';
import { getMessageResources } from '../selectors/messageSelector';

export interface MessageResourceProps {
    resources?: MessageResourceInput[];
}

export enum MessageResourceTestIds {
    MessageResource = 'MessageResourceTestIds.MessageResource',
    BenefitResource = 'MessageResourceTestIds.BenefitResource',
}

function getResourceIdFromNode(
    node: MeditationNode | ProgramNode | CompanyAdditionalBenefitFragment,
) {
    switch (node.__typename) {
        case 'CompanyAdditionalBenefitNode':
            return 'benefit';
        case 'ProgramNode':
            return 'program';
        case 'MeditationNode':
            return 'meditation';
        default:
            return;
    }
}

export const MessageResource: FC<MessageResourceProps> = ({ resources }) => {
    const messageResources = useSelector(getMessageResources);
    const benefitResources = useSelector(getCompanyAdditionalBenefitIdsMap);
    const { t } = useTranslation();
    const [selectedBenefit, setSelectedBenefit] = useState<Benefit | undefined>(undefined);
    const [isBenefitModalVisible, setIsBenefitModalVisible] = useState(false);

    if (!resources || resources.length < 1) {
        return null;
    }

    const getMappedResource = (
        resource: MessageResourceInput,
    ): (MeditationNode | ProgramNode | CompanyAdditionalBenefitFragment) | undefined => {
        if (!messageResources && !benefitResources) return undefined;
        switch (true) {
            case !!resource?.meditationId:
                return messageResources.meditations?.[resource.meditationId ?? ''];
            case !!resource?.programId:
                return messageResources.programs?.[resource.programId ?? ''];
            case !!resource?.companyAdditionalBenefitId:
                return benefitResources[resource.companyAdditionalBenefitId ?? ''];
            default:
                return undefined;
        }
    };

    return (
        <>
            {resources.map(r => {
                const resource = getMappedResource(r);
                if (!resource) return null;
                const resourceType =
                    resource.__typename === 'CompanyAdditionalBenefitNode'
                        ? 'Benefit'
                        : resource.__typename?.replace('Node', '');
                const resourceTypeLabel = t(
                    `messaging:conversation.resource.resource_type.${getResourceIdFromNode(
                        resource,
                    )}`,
                    resourceType,
                );
                const isABenefit = resource.__typename === 'CompanyAdditionalBenefitNode';
                const isAProgram = resource.__typename === 'ProgramNode';
                const isAMeditation = resource.__typename === 'MeditationNode';

                return (
                    <div
                        key={`${resource.__typename}${resource.id}`}
                        data-testid={MessageResourceTestIds.MessageResource}
                    >
                        {(isAMeditation || isAProgram) && (
                            <StyledResourceContainer
                                aria-label={t(
                                    'messaging:conversation.resource.resource_type_link.arial_label',
                                    {
                                        resourceType: resourceTypeLabel,
                                        resourceName: resource.name,
                                        duration: resource.duration,
                                    },
                                )}
                                onClick={() => {
                                    window.open(
                                        `/${resourceType?.toLowerCase()}/${resource.slug}`,
                                        '_blank',
                                    );
                                }}
                            >
                                <StyledResourceImage
                                    height={50}
                                    src={resource.image ?? ''}
                                    aria-hidden="true"
                                    alt={t(
                                        'messaging:conversation.resource.resource_type_image.alt_text',
                                        {
                                            resourceType: resourceTypeLabel,
                                        },
                                    )}
                                />
                                <div>
                                    <HeadingText10 marginBottom={0} fontColor="gray85">
                                        {resource.name}
                                    </HeadingText10>
                                    <StyledResourceDetails>
                                        {isAMeditation && (
                                            <StyledIconSpan>
                                                <StyledIcon
                                                    aria-hidden="true"
                                                    src={audioIcon}
                                                    alt={t(
                                                        'messaging:conversation.resource.audio_icon',
                                                    )}
                                                />
                                            </StyledIconSpan>
                                        )}
                                        <StyledResourceType isAMeditation={isAMeditation}>
                                            {resourceTypeLabel}
                                        </StyledResourceType>
                                        <StyledDot />
                                        <span>
                                            {t('messaging:conversation.resource.duration_label', {
                                                duration: resource.duration,
                                            })}
                                        </span>
                                    </StyledResourceDetails>
                                </div>
                            </StyledResourceContainer>
                        )}
                        {isABenefit && (
                            <StyledResourceContainer
                                data-testid={MessageResourceTestIds.BenefitResource}
                                onClick={() => {
                                    setSelectedBenefit({
                                        ...resource,
                                        imageUrl: resource.image || '',
                                    } as Benefit);
                                    setIsBenefitModalVisible(true);
                                }}
                            >
                                <BenefitImageContainer color={resource.color || ''}>
                                    <BenefitImage
                                        height={50}
                                        src={resource.image ?? ''}
                                        aria-hidden="true"
                                        alt={t(
                                            'messaging:conversation.resource.resource_type_image.alt_text',
                                            {
                                                resourceType: resourceTypeLabel,
                                            },
                                        )}
                                    />
                                </BenefitImageContainer>
                                <div>
                                    <HeadingText10 marginBottom={0} fontColor="gray85">
                                        {resource.title}
                                    </HeadingText10>
                                    <StyledResourceDetails>
                                        <StyledResourceType isAMeditation={isAMeditation}>
                                            {resourceTypeLabel}
                                        </StyledResourceType>
                                    </StyledResourceDetails>
                                </div>
                            </StyledResourceContainer>
                        )}
                    </div>
                );
            })}
            <BenefitModal
                onClose={() => setIsBenefitModalVisible(false)}
                benefit={selectedBenefit}
                visible={isBenefitModalVisible}
            ></BenefitModal>
        </>
    );
};

const StyledResourceContainer = styled.div`
    border: 1px solid ${colors.gray10};
    border-radius: 6px;
    padding: ${pxToRem(9)};
    margin: ${pxToRem(12, 0)};
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const StyledIcon = styled.img`
    user-select: none;
`;

const StyledIconSpan = styled.span`
    display: flex;
`;

const StyledResourceImage = styled.img`
    margin-right: ${pxToRem(12)};
`;

const StyledResourceDetails = styled.p`
    display: flex;
    align-items: center;
`;

interface StyledResourceTypeProps {
    isAMeditation: boolean;
}

const StyledResourceType = styled.span<StyledResourceTypeProps>`
    margin-left: ${props => (props.isAMeditation ? pxToRem(5) : 0)};
`;

interface BenefitImageContainerProp {
    color: string;
}

const BenefitImageContainer = styled.div<BenefitImageContainerProp>`
    height: ${pxToRem(56)};
    width: ${pxToRem(56)};
    border-radius: ${pxToRem(5)};
    background-color: ${props => props.color};
    margin-right: ${pxToRem(12)};
`;

const BenefitImage = styled.img`
    margin-top: ${pxToRem(12)};
    margin-left: ${pxToRem(12)};
    width: 60%;
    height: 60%;
`;

const StyledDot = styled.span`
    background-color: ${colors.gray55};
    width: ${pxToRem(3)};
    height: ${pxToRem(3)};
    margin: ${pxToRem(0, 5)};
    border-radius: 50%;
`;
